// SchemaFieldInput — renders a single CmForm schema field (`SchemaField`) as an
// editable input, driven by the field's `widget`. Shared by the form-builder
// record editor and (upcoming) the map EntityForm so schema-driven capture is
// rendered identically everywhere.
//
// Handles data widgets only (text/textarea/number/select/multiselect/
// classification/date/datetime/time/yesno/barcode/hyperlink/address/location).
// Media widgets (photo/video/audio, file attachment, signature, sketch) are
// handled by their host's media flow, not here.

import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import LinkIcon from "@mui/icons-material/Link";
import { ClassificationOption, SchemaField } from "../../../types/fieldForm";
import { slugifyValue } from "../../../services/classificationSets";
import { useClassificationSets } from "../../../api/classificationSets";
import { describeAcceptableInput, validateFieldFormat } from "../../../utilities/formBuilder/captureValidation";
import { formatCalculatedValue } from "../../../utilities/formBuilder/calculatedFormat";

/** Sentinel option value marking the "Other — free text" choice. */
const OTHER_VALUE = "__other__";

export interface SchemaFieldInputProps {
  field: SchemaField;
  value: unknown;
  onChange: (v: unknown) => void;
}

/** Shared input styling — mirrors EntityForm's Name/Description TextFields. */
const inputSx = {
  "& .MuiInputBase-input": {
    padding: "0.94rem 0.875rem",
    height: "auto"
  },
  "& .MuiInputBase-inputMultiline": {
    padding: 0
  },
  "& fieldset": {
    borderColor: "grey.300",
    borderRadius: "0.5rem"
  }
} as const;

/** Field label rendered above the control, matching the Name/Description labels. */
const FieldLabel: React.FC<{ field: SchemaField }> = ({ field }) => (
  <Typography variant='body1' component='label' sx={{ color: "grey.600" }}>
    {field.label || field.key}
    {field.required && (
      <Box component='span' sx={{ color: "error.main", ml: "0.25rem" }}>
        *
      </Box>
    )}
  </Typography>
);

export const SchemaFieldInput: React.FC<SchemaFieldInputProps> = ({ field, value, onChange }) => {
  if (field.widget === "calculated") {
    return (
      <FormControl fullWidth sx={{ gap: "0.37rem" }}>
        <FieldLabel field={field} />
        <TextField fullWidth value={formatCalculatedValue(field, value)} disabled sx={inputSx} />
      </FormControl>
    );
  }

  // Yes / No toggle (with optional N/A + read-only lock)
  if (field.widget === "yesno") {
    const v = value === true || value === "yes" ? "yes" : value === false || value === "no" ? "no" : value === "na" ? "na" : "";
    return (
      <FormControl fullWidth sx={{ gap: "0.37rem" }}>
        <FieldLabel field={field} />
        <ToggleButtonGroup
          exclusive
          size='small'
          value={v}
          disabled={field.readonly}
          onChange={(_e, next) => {
            if (next == null) return;
            onChange(next === "yes" ? true : next === "no" ? false : "na");
          }}
          sx={{ "& .MuiToggleButton-root": { px: "1.25rem", borderColor: "grey.300", textTransform: "none" } }}
        >
          <ToggleButton value='yes'>{field.positive_label || "Yes"}</ToggleButton>
          <ToggleButton value='no'>{field.negative_label || "No"}</ToggleButton>
          {field.na_enabled && <ToggleButton value='na'>N/A</ToggleButton>}
        </ToggleButtonGroup>
      </FormControl>
    );
  }

  // Classification — indented checkbox tree (single leaf value, or multiple).
  if (field.widget === "classification") {
    return <ClassificationInput field={field} value={value} onChange={onChange} />;
  }

  // Single / multiple choice — options come from `field.options` (custom) or a
  // referenced choice list resolved live by id (source_meta.choice_list_id).
  if (field.widget === "select" || field.widget === "multiselect") {
    return <ChoiceSelectInput field={field} value={value} onChange={onChange} multi={field.widget === "multiselect"} />;
  }

  // Date / datetime / time
  const nativeType =
    field.widget === "date" ? "date" : field.widget === "datetime" ? "datetime-local" : field.widget === "time" ? "time" : null;
  if (nativeType) {
    return (
      <FormControl fullWidth sx={{ gap: "0.37rem" }}>
        <FieldLabel field={field} />
        <TextField
          fullWidth
          type={nativeType}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={inputSx}
        />
      </FormControl>
    );
  }

  // Default — text / textarea / number / barcode / hyperlink / address / location
  const multiline = field.widget === "textarea";
  const numeric = field.widget === "number";
  const isLink = field.widget === "hyperlink";
  const strVal = String(value ?? "");
  const validationError = validateFieldFormat(field, strVal);
  return (
    <FormControl fullWidth sx={{ gap: "0.37rem" }}>
      <FieldLabel field={field} />
      <TextField
        fullWidth
        multiline={multiline}
        rows={multiline ? 4 : undefined}
        type={numeric ? "number" : isLink ? "url" : "text"}
        value={strVal}
        onChange={(e) => onChange(e.target.value)}
        disabled={field.readonly}
        error={!!validationError}
        helperText={validationError || describeAcceptableInput(field) || undefined}
        placeholder={isLink ? "https://example.com" : undefined}
        InputProps={
          isLink
            ? {
                startAdornment: (
                  <InputAdornment position='start'>
                    <LinkIcon fontSize='small' sx={{ color: "grey.500" }} />
                  </InputAdornment>
                )
              }
            : undefined
        }
        inputProps={{
          ...(field.max_length != null && !numeric ? { maxLength: field.max_length } : {}),
          ...(numeric && field.min != null ? { min: field.min } : {}),
          ...(numeric && field.max != null ? { max: field.max } : {}),
          // Integer format disallows decimals via the numeric spinner step.
          ...(numeric ? { step: field.format === "integer" ? 1 : "any" } : {})
        }}
        sx={{
          ...inputSx,
          ...(multiline && { "& .MuiInputBase-root": { padding: "0.94rem 0.875rem" } })
        }}
      />
    </FormControl>
  );
};

// ─── Single / multiple choice (custom options or a referenced choice list) ───

/** Reads the referenced choice-list id off a field's `source_meta`. */
function readChoiceListId(field: SchemaField): string | undefined {
  return (field.source_meta as { choice_list_id?: string } | undefined)?.choice_list_id;
}

/** Flatten a choice-list tree to `{label, value}` pairs (lists are flat, but be safe). */
function flattenChoiceOptions(
  nodes: ClassificationOption[],
  acc: { label: string; value: string }[] = []
): { label: string; value: string }[] {
  for (const n of nodes) {
    const value = n.value || slugifyValue(n.label || "");
    if (value) acc.push({ label: n.label || value, value });
    if (n.children?.length) flattenChoiceOptions(n.children, acc);
  }
  return acc;
}

interface ChoiceSelectInputProps {
  field: SchemaField;
  value: unknown;
  onChange: (v: unknown) => void;
  multi: boolean;
}

/**
 * Single/multiple choice input. Options come from `field.options` (custom, stored
 * in the schema JSON) or a referenced choice list resolved live by id from the
 * classification-sets API (`source_meta.choice_list_id`) — mirroring classification.
 */
const ChoiceSelectInput: React.FC<ChoiceSelectInputProps> = ({ field, value, onChange, multi }) => {
  const listId = readChoiceListId(field);
  const { data: lists = [] } = useClassificationSets("choice_list");
  const [otherMode, setOtherMode] = useState(false);
  const [otherDraft, setOtherDraft] = useState("");

  const opts = useMemo<{ label: string; value: string }[]>(() => {
    if (listId) {
      const list = lists.find((l) => l.id === listId);
      return list ? flattenChoiceOptions(list.tree ?? []) : [];
    }
    return (field.options ?? []).map((o) => ({ label: o, value: o }));
  }, [listId, lists, field.options]);

  const labelFor = (v: string) => opts.find((o) => o.value === v)?.label ?? v;
  const isKnown = (v: string) => opts.some((o) => o.value === v);
  const allowOther = !!field.allow_other;
  const readonly = !!field.readonly;

  if (!opts.length) {
    return (
      <FormControl fullWidth sx={{ gap: "0.37rem" }}>
        <FieldLabel field={field} />
        <Typography variant='body2' color='text.disabled'>
          {listId ? "Choice list unavailable or empty." : "No options configured for this field."}
        </Typography>
      </FormControl>
    );
  }

  if (!multi) {
    const strValue = String(value ?? "");
    const isOther = !!strValue && !isKnown(strValue);
    const showOther = allowOther && (isOther || otherMode);
    const selectValue = showOther ? OTHER_VALUE : isKnown(strValue) ? strValue : "";
    return (
      <FormControl fullWidth sx={{ gap: "0.37rem" }}>
        <FieldLabel field={field} />
        <TextField
          select
          fullWidth
          value={selectValue}
          onChange={(e) => {
            // Read-only: dropdown opens for inspection, but selection is ignored.
            if (readonly) return;
            const val = e.target.value;
            if (val === OTHER_VALUE) {
              setOtherMode(true);
              onChange("");
            } else {
              setOtherMode(false);
              onChange(val);
            }
          }}
          sx={inputSx}
        >
          <MenuItem value=''>
            <em>Select…</em>
          </MenuItem>
          {opts.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
          {allowOther && <MenuItem value={OTHER_VALUE}>Other…</MenuItem>}
        </TextField>
        {showOther && (
          <TextField
            fullWidth
            placeholder='Enter other value'
            value={isOther ? strValue : ""}
            disabled={readonly}
            onChange={(e) => onChange(e.target.value)}
            sx={inputSx}
          />
        )}
      </FormControl>
    );
  }

  const arr = Array.isArray(value) ? (value as string[]) : value ? [String(value)] : [];
  // Custom (non-list) values so they remain toggleable/removable in the menu.
  const customVals = allowOther ? arr.filter((v) => !isKnown(v)) : [];
  const menuOpts = [...opts, ...customVals.map((v) => ({ label: v, value: v }))];
  const selectionError = validateFieldFormat(field, arr);

  const addOther = () => {
    const v = otherDraft.trim();
    if (!v || arr.includes(v)) {
      setOtherDraft("");
      return;
    }
    onChange([...arr, v]);
    setOtherDraft("");
  };

  return (
    <FormControl fullWidth sx={{ gap: "0.37rem" }}>
      <FieldLabel field={field} />
      <Select
        multiple
        fullWidth
        displayEmpty
        value={arr}
        onChange={(e) => {
          // Read-only: dropdown opens for inspection, but selection is ignored.
          if (readonly) return;
          onChange(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value);
        }}
        error={!!selectionError}
        renderValue={(sel) => {
          const vals = sel as string[];
          if (!vals.length) return <Box sx={{ color: "text.disabled" }}>Select…</Box>;
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
              {vals.map((v) => (
                <Chip key={v} label={labelFor(v)} size='small' />
              ))}
            </Box>
          );
        }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
        sx={{
          "& .MuiSelect-select": { padding: "0.75rem 0.875rem", minHeight: "auto" },
          "& fieldset": { borderColor: "grey.300", borderRadius: "0.5rem" }
        }}
      >
        {menuOpts.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <Checkbox checked={arr.indexOf(opt.value) > -1} size='small' sx={{ p: 0, mr: "0.625rem" }} />
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {allowOther && !readonly && (
        <Stack direction='row' spacing={"0.5rem"} alignItems='center'>
          <TextField
            fullWidth
            size='small'
            placeholder='Add other value'
            value={otherDraft}
            onChange={(e) => setOtherDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addOther();
              }
            }}
            sx={inputSx}
          />
          <Button variant='outlined' size='small' startIcon={<AddIcon />} onClick={addOther} disabled={!otherDraft.trim()}>
            Add
          </Button>
        </Stack>
      )}
      {selectionError && (
        <Typography variant='caption' color='error.main'>
          {selectionError}
        </Typography>
      )}
      {!selectionError && describeAcceptableInput(field) && (
        <Typography variant='caption' color='text.secondary'>
          {describeAcceptableInput(field)}
        </Typography>
      )}
    </FormControl>
  );
};

// ─── Classification (indented checkbox tree) ─────────────────────────────────

/** Reads the referenced classification set id off a field's `source_meta`. */
function readClassificationSetId(field: SchemaField): string | undefined {
  return (field.source_meta as { classification_set_id?: string } | undefined)?.classification_set_id;
}

/** All leaf `value`s beneath a node (a node with no children is its own leaf). */
function leafValuesOf(node: ClassificationOption): string[] {
  const value = node.value || slugifyValue(node.label || "");
  if (!node.children?.length) return [value];
  return node.children.flatMap(leafValuesOf);
}

/**
 * Filters a classification tree to nodes matching `q` (case-insensitive). A
 * matching category keeps its full subtree; a non-matching category is kept only
 * if some descendant matches (and is pruned to those matches).
 */
function filterTree(nodes: ClassificationOption[], q: string): ClassificationOption[] {
  const out: ClassificationOption[] = [];
  for (const node of nodes) {
    const label = (node.label || node.value || "").toLowerCase();
    const selfMatch = label.includes(q);
    const filteredChildren = node.children?.length ? filterTree(node.children, q) : [];
    if (selfMatch) out.push(node);
    else if (filteredChildren.length) out.push({ ...node, children: filteredChildren });
  }
  return out;
}

interface ClassificationInputProps {
  field: SchemaField;
  value: unknown;
  onChange: (v: unknown) => void;
}

/**
 * Renders the referenced classification set as an indented, collapsible tree with
 * checkboxes and a search box. The set is resolved **live** from the
 * classification-sets API (never snapshotted into the form schema).
 *
 * - Single (default): selecting a leaf stores its `value` string (checkboxes behave
 *   as a single choice — picking a new leaf replaces the previous one). A legacy
 *   path array is read back by its last element.
 * - Multiple (`field.multiple`): every node is selectable; ticking a category toggles
 *   all of its descendant leaves. Stores an array of leaf `value` strings.
 */
const ClassificationInput: React.FC<ClassificationInputProps> = ({ field, value, onChange }) => {
  const { data: sets = [], isLoading } = useClassificationSets("classification_set");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [otherDraft, setOtherDraft] = useState("");

  const setId = readClassificationSetId(field);
  const set = sets.find((s) => s.id === setId);
  const multiple = !!field.multiple;
  const readonly = !!field.readonly;
  const allowOther = !!field.allow_other;

  const query = search.trim().toLowerCase();
  const searching = query.length > 0;
  const visibleTree = useMemo(() => {
    const tree = set?.tree ?? [];
    return searching ? filterTree(tree, query) : tree;
  }, [set, searching, query]);

  // Normalise the stored value to a list of selected leaf values.
  const selected: string[] = multiple
    ? Array.isArray(value)
      ? (value as unknown[]).map(String)
      : value
        ? [String(value)]
        : []
    : Array.isArray(value)
      ? [String((value as unknown[])[value.length - 1] ?? "")].filter(Boolean)
      : value
        ? [String(value)]
        : [];
  const selectedSet = new Set(selected);

  const wrap = (children: React.ReactNode) => (
    <FormControl fullWidth sx={{ gap: "0.37rem" }}>
      <FieldLabel field={field} />
      {children}
    </FormControl>
  );

  if (!setId) {
    return wrap(
      <Typography variant='caption' color='text.secondary'>
        No classification set selected for this field.
      </Typography>
    );
  }

  if (isLoading && !set) {
    return wrap(
      <Typography variant='caption' color='text.secondary'>
        Loading classification set…
      </Typography>
    );
  }

  if (!set) {
    return wrap(
      <Typography variant='caption' color='error.main'>
        Classification set unavailable{selected.length ? ` (stored: ${selected.join(", ")})` : ""}.
      </Typography>
    );
  }

  const toggleExpand = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Single-select: pick exactly this leaf (click again to clear).
  const selectLeaf = (leafValue: string) => {
    if (readonly) return;
    onChange(selectedSet.has(leafValue) ? "" : leafValue);
  };

  // Multi-select: toggle a set of leaf values on/off, preserving insertion order.
  const toggleValues = (values: string[], turnOn: boolean) => {
    if (readonly) return;
    const next = new Set(selectedSet);
    for (const v of values) {
      if (turnOn) next.add(v);
      else next.delete(v);
    }
    onChange(Array.from(next));
  };

  // "Other" free-text values are the stored selections not present as leaves in the set.
  const treeLeaves = new Set((set.tree ?? []).flatMap(leafValuesOf));
  const otherValues = selected.filter((v) => !treeLeaves.has(v));
  // Single-select: the other text replaces the whole value.
  const setOther = (raw: string) => {
    if (readonly) return;
    onChange(raw.trim());
  };
  // Multi-select: append / remove an individual custom value.
  const addOther = () => {
    if (readonly) return;
    const text = otherDraft.trim();
    if (!text || selected.includes(text)) {
      setOtherDraft("");
      return;
    }
    onChange([...selected, text]);
    setOtherDraft("");
  };
  const removeOther = (v: string) => {
    if (readonly) return;
    onChange(selected.filter((x) => x !== v));
  };

  return wrap(
    <Box
      sx={{
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: "0.5rem",
        overflow: "hidden"
      }}
    >
      <Box sx={{ p: "0.5rem", borderBottom: "1px solid", borderColor: "grey.200" }}>
        <TextField
          size='small'
          fullWidth
          placeholder='Search…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' sx={{ color: "grey.500" }} />
              </InputAdornment>
            )
          }}
          sx={{ "& fieldset": { borderColor: "grey.300", borderRadius: "0.5rem" } }}
        />
      </Box>
      <Box sx={{ maxHeight: 280, overflowY: "auto", p: "0.5rem 0.75rem" }} className='scroller'>
        {visibleTree.length ? (
          <ClassificationNodes
            nodes={visibleTree}
            depth={0}
            parentKey=''
            multiple={multiple}
            readonly={readonly}
            selectedSet={selectedSet}
            expandedSet={expanded}
            forceExpand={searching}
            onToggleExpand={toggleExpand}
            onSelectLeaf={selectLeaf}
            onToggleValues={toggleValues}
          />
        ) : (
          <Typography variant='caption' color='text.secondary'>
            No matches.
          </Typography>
        )}
      </Box>
      {allowOther && (
        <Box sx={{ p: "0.5rem 0.75rem", borderTop: "1px solid", borderColor: "grey.200" }}>
          {multiple ? (
            <Stack spacing={"0.5rem"}>
              {otherValues.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
                  {otherValues.map((v) => (
                    <Chip key={v} label={v} size='small' onDelete={readonly ? undefined : () => removeOther(v)} />
                  ))}
                </Box>
              )}
              {!readonly && (
                <Stack direction='row' spacing={"0.5rem"} alignItems='center'>
                  <TextField
                    size='small'
                    fullWidth
                    placeholder='Add other value'
                    value={otherDraft}
                    onChange={(e) => setOtherDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addOther();
                      }
                    }}
                    sx={{ "& fieldset": { borderColor: "grey.300", borderRadius: "0.5rem" } }}
                  />
                  <Button variant='outlined' size='small' startIcon={<AddIcon />} onClick={addOther} disabled={!otherDraft.trim()}>
                    Add
                  </Button>
                </Stack>
              )}
            </Stack>
          ) : (
            <TextField
              size='small'
              fullWidth
              placeholder='Other (free text)'
              value={otherValues[0] ?? ""}
              disabled={readonly}
              onChange={(e) => setOther(e.target.value)}
              sx={{ "& fieldset": { borderColor: "grey.300", borderRadius: "0.5rem" } }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

interface ClassificationNodesProps {
  nodes: ClassificationOption[];
  depth: number;
  parentKey: string;
  multiple: boolean;
  readonly?: boolean;
  selectedSet: Set<string>;
  expandedSet: Set<string>;
  forceExpand: boolean;
  onToggleExpand: (key: string) => void;
  onSelectLeaf: (leafValue: string) => void;
  onToggleValues: (values: string[], turnOn: boolean) => void;
}

/** Width reserved for the category expand/collapse toggle, so leaf rows align under it. */
const TOGGLE_WIDTH = "1.75rem";

/** Recursive renderer for the classification tree (indented, collapsible rows). */
const ClassificationNodes: React.FC<ClassificationNodesProps> = ({
  nodes,
  depth,
  parentKey,
  multiple,
  readonly,
  selectedSet,
  expandedSet,
  forceExpand,
  onToggleExpand,
  onSelectLeaf,
  onToggleValues
}) => {
  return (
    <>
      {nodes.map((node, i) => {
        const value = node.value || slugifyValue(node.label || "");
        const key = `${parentKey}/${value}-${i}`;
        const isCategory = !!node.children?.length;
        const label = node.label || node.value || "(unnamed)";
        const indent = `${depth * 1}rem`;

        if (isCategory) {
          const leaves = leafValuesOf(node);
          const checkedCount = leaves.filter((l) => selectedSet.has(l)).length;
          const allChecked = leaves.length > 0 && checkedCount === leaves.length;
          const someChecked = checkedCount > 0 && !allChecked;
          const isOpen = forceExpand || expandedSet.has(key);
          return (
            <Box key={key}>
              <Stack
                direction='row'
                alignItems='center'
                sx={{
                  pl: indent,
                  py: "1px",
                  borderRadius: "0.375rem",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" }
                }}
                onClick={() => onToggleExpand(key)}
              >
                <IconButton
                  size='small'
                  disableRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpand(key);
                  }}
                  sx={{ p: "0.125rem", mr: "0.125rem", color: "grey.500" }}
                >
                  {isOpen ? <ExpandMoreIcon fontSize='small' /> : <ChevronRightIcon fontSize='small' />}
                </IconButton>
                {multiple && (
                  <Checkbox
                    size='small'
                    checked={allChecked}
                    indeterminate={someChecked}
                    disabled={readonly}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onToggleValues(leaves, e.target.checked)}
                    sx={{ p: "0.25rem", mr: "0.25rem" }}
                  />
                )}
                <Typography variant='body2' sx={{ fontWeight: 600, color: "grey.700" }}>
                  {label}
                </Typography>
              </Stack>
              <Collapse in={isOpen} timeout='auto' unmountOnExit>
                <ClassificationNodes
                  nodes={node.children as ClassificationOption[]}
                  depth={depth + 1}
                  parentKey={key}
                  multiple={multiple}
                  readonly={readonly}
                  selectedSet={selectedSet}
                  expandedSet={expandedSet}
                  forceExpand={forceExpand}
                  onToggleExpand={onToggleExpand}
                  onSelectLeaf={onSelectLeaf}
                  onToggleValues={onToggleValues}
                />
              </Collapse>
            </Box>
          );
        }

        // Leaf — selectable.
        const checked = selectedSet.has(value);
        return (
          <Stack
            key={key}
            direction='row'
            alignItems='center'
            sx={{
              pl: indent,
              py: "1px",
              borderRadius: "0.375rem",
              cursor: readonly ? "default" : "pointer",
              "&:hover": readonly ? {} : { bgcolor: "action.hover" }
            }}
            onClick={() => {
              if (readonly) return;
              multiple ? onToggleValues([value], !checked) : onSelectLeaf(value);
            }}
          >
            <Box sx={{ width: TOGGLE_WIDTH, flexShrink: 0 }} />
            <Checkbox size='small' checked={checked} disabled={readonly} sx={{ p: "0.25rem", mr: "0.25rem" }} />
            <Typography variant='body2' sx={{ color: "text.primary" }}>
              {label}
            </Typography>
          </Stack>
        );
      })}
    </>
  );
};

export default SchemaFieldInput;
